import MenuIcon from "@mui/icons-material/Menu";
import { Drawer as MuiDrawer, styled, Theme, useMediaQuery } from "@mui/material";
import { useFeatureGate } from "@statsig/react-bindings";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import BrandEquity from "@/Components/Icons/BrandEquity";
import { IconButton } from "@/Components/v2/atoms/Button";
import { Campaign } from "@/Components/Icons/Campaign";
// Icons
import FinancialReview from "@/Components/Icons/FinancialReview";
import { FlightingIcon } from "@/Components/Icons/FlightingIcon";
import Formats from "@/Components/Icons/Formats";
import { Geo } from "@/Components/Icons/Geo";
import MROI from "@/Components/Icons/MROI";
import Publishers from "@/Components/Icons/Publishers";
import Rocket from "@/Components/Icons/Rocket";
import Sales from "@/Components/Icons/Sales";
import SatCurve from "@/Components/Icons/SatCurve";
import { Box } from "@/Components/v2/atoms/Box";
import { List } from "@/Components/v2/atoms/List";
import { LinkType } from "@/Components/v2/constants/enum";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { DRAWER_BREAKPOINT, DRAWER_WIDTH_PX } from "@/Utils/GlobalStyle";
import { hasAccess } from "@/Utils/ShowComponent";

import MenuGroup from "./MenuGroup";
import { DrawerHeaderV2 } from "../../molecule/DrawerHeader/DrawerHeaderV2";

const SUBHEADER_TEXT = "GrowthOS";

interface MenuListItem {
  id: string;
  label: string;
  route: string;
  activePaths: string[];
  onClick?: () => void;
  hasAccess: boolean;
  isBeta?: boolean;
  type?: LinkType;
  icon?: React.ReactNode;
}

export interface MenuListGroup {
  id: string;
  label: string;
  items: MenuListItem[];
}

enum DrawerType {
  Permanent = "permanent",
  Temporary = "temporary",
  Persistant = "persistant",
}

const StyledDrawer = styled(MuiDrawer)(({ theme }) => {
  return {
    flexShrink: 0,
    "& .MuiDrawer-paper": {
      width: DRAWER_WIDTH_PX,
      padding: "12px 20px",
      borderRight: "none",
      boxSizing: "border-box",
      backgroundImage: "none",
      boxShadow: `0px 4px 4px 0px ${theme.palette.navigation.border}`,
    },
    "& .MuiBackdrop-root": {
      backgroundColor: "transparent",
    },
    "& .MuiDrawer-root": {
      position: "absolute",
    },
    "& .MuiPaper-root": {
      position: "absolute",
      height: "100%",
      [`@media (max-width: ${DRAWER_BREAKPOINT - 1}px)`]: {
        top: "60px",
      },
    },
  };
});

const StyledIconButton = styled(IconButton)(({ theme }) => {
  return {
    borderRadius: "4px",
    width: "50px",
    height: "50px",
    margin: "20px",
    border: `1px solid ${theme.palette.navigation.buttonBorder}`,
  };
});

export const DrawerV2: React.FC = () => {
  const [isOpen, setIsOpen] = useState(true);
  const isLargerScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up(DRAWER_BREAKPOINT));
  const variant = isLargerScreen ? DrawerType.Permanent : DrawerType.Temporary;
  const location = useLocation();
  const { value: isGeographiesEnabled } = useFeatureGate(featureFlag.geographies);
  const { value: isCampaignInsightsEnabled } = useFeatureGate(featureFlag.campaignInsights);
  const { value: isBrandEquityEnabled } = useFeatureGate(featureFlag.brandEquity);
  const { value: isGrowthCentreEnabled } = useFeatureGate(featureFlag.growthCentre);
  const { loading, userData: user } = useInfo();

  const toggleDrawer = (newOpen: boolean) => () => {
    setIsOpen(newOpen);
  };

  const menuListGroups: MenuListGroup[] = [
    ...(isGrowthCentreEnabled
      ? [
          {
            id: "growth-center",
            label: "",
            items: [
              {
                id: "growth-center-home",
                label: "Growth Center",
                route: "/growth-center",
                activePaths: ["/growth-center"],
                hasAccess: hasAccess(user, "Viewer"),
                icon: <Rocket fontSize="small" />,
                type: LinkType.Internal,
              },
            ],
          },
        ]
      : []),
    {
      id: "media-decisions",
      label: "Media Decisions",
      items: [
        {
          id: "dashboard",
          label: "Channel ROI",
          route: "/channel-roi",
          activePaths: ["/channel-roi"],
          hasAccess: hasAccess(user, "Viewer"),
          icon: <MROI fontSize="small" />,
          type: LinkType.Internal,
        },

        {
          id: "investment-levels-v3",
          label: "Investment Levels",
          route: "/investment-levels",
          activePaths: ["/investment-levels"],
          hasAccess: hasAccess(user, "Viewer"),
          icon: <SatCurve fontSize="small" />,
          type: LinkType.Internal,
        },
        {
          id: "flighting",
          label: "Flighting",
          route: "/flighting",
          activePaths: ["/flighting"],
          hasAccess: hasAccess(user, "Viewer"),
          icon: <FlightingIcon fontSize="small" />,
          type: LinkType.Internal,
        },

        {
          id: "geographies",
          label: "Geographies",
          route: "/geographies",
          activePaths: ["/geographies"],
          icon: <Geo fontSize="small" />,
          hasAccess: hasAccess(user, "Viewer") && isGeographiesEnabled,
          type: LinkType.Internal,
        },

        {
          id: "campaigns",
          label: "Campaigns",
          route: "/campaigns",
          activePaths: ["/campaigns"],
          hasAccess: hasAccess(user, "Viewer") && isCampaignInsightsEnabled,
          icon: <Campaign fontSize="small" />,
          type: LinkType.Internal,
        },
      ],
    },
    {
      id: "sales-analysis",
      label: "Sales Analysis",
      items: [
        {
          id: "sales-drivers",
          label: "Sales Drivers",
          route: "/sales-drivers",
          activePaths: ["/sales-drivers"],
          hasAccess: hasAccess(user, "Viewer"),
          icon: <Sales fontSize="small" />,
          type: LinkType.Internal,
        },

        {
          id: "financial-review",
          label: "Financial Review",
          route: "/financial-review",
          activePaths: ["/financial-review"],
          hasAccess: hasAccess(user, "Viewer"),
          icon: <FinancialReview fontSize="small" />,
          type: LinkType.Internal,
        },

        {
          id: "brand-equity",
          label: "Brand Equity",
          route: "/brand-equity",
          activePaths: ["/brand-equity"],
          hasAccess: hasAccess(user, "Viewer") && isBrandEquityEnabled,
          icon: <BrandEquity fontSize="small" />,
          type: LinkType.Internal,
        },
      ],
    },
    {
      id: "channel-analysis",
      label: "Channel Analysis",
      items: [
        {
          id: "publishers",
          label: "Publishers",
          route: "/publishers",
          activePaths: ["/publishers"],
          hasAccess: hasAccess(user, "Viewer"),
          icon: <Publishers fontSize="small" />,
          type: LinkType.Internal,
        },

        {
          id: "formats",
          label: "Formats",
          route: "/formats",
          activePaths: ["/formats"],
          hasAccess: hasAccess(user, "Viewer"),
          icon: <Formats fontSize="small" />, // change later
          type: LinkType.Internal,
        },
      ],
    },
  ];

  if (loading) {
    return null;
  }

  const isSelected = (routes: string[]) => {
    return routes.includes(location.pathname);
  };

  return (
    <Box position="relative" height="100%" overflow="hidden">
      {!isLargerScreen && (
        <Box position="fixed" zIndex={1100}>
          <StyledIconButton
            onClick={toggleDrawer(true)}
            aria-label="open drawer"
            edge="start"
            color="inherit"
            data-testcomp="OpenDrawerV2"
          >
            <MenuIcon sx={{ fontSize: 24 }} />
          </StyledIconButton>
        </Box>
      )}
      <StyledDrawer open={isOpen} onClose={toggleDrawer(false)} variant={variant}>
        <DrawerHeaderV2
          title={SUBHEADER_TEXT}
          toggleDrawer={toggleDrawer(false)}
          hideCloseIcon={isLargerScreen}
        />
        <List>
          {menuListGroups.map((group) => {
            return <MenuGroup key={group.id} group={group} isSelected={isSelected} />;
          })}
        </List>
      </StyledDrawer>
    </Box>
  );
};

export default DrawerV2;
